import {
  CardItemContent,
  CardItemIcon,
  CardList,
  CardListItem,
  CardSubtitle,
  CardTitle,
} from '@/components/Card'
import type { IconName } from '@/components/Icon'
import { LabelChip } from '@/components/Chip'
import {
  ModalDialog,
  ModalDialogActions,
  ModalDialogContent,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
} from '@/components/ModalDialog'
import { Button } from '@/components/Button'
import type { ReactNode } from 'react'
import type {
  CallOption,
  CallOptionId,
} from '@/features/dashboard/components/CallAssistanceDialogs/index'
import { formatUSPhoneNumber } from '@/utils/phone-number-util'
import styles from './CallCenterAssistance.module.css'

interface CallOptionTypesDialogProps {
  isOpen: boolean
  children?: ReactNode
  onClose: () => void
  isNextDisabled: boolean
  options: CallOption[]
  onNext: () => void
  selectOption: (option: CallOptionId) => void
  activeId: CallOptionId | null
}

export function CallOptionTypesDialog({
  isOpen,
  onClose,
  isNextDisabled,
  onNext,
  options,
  selectOption,
  activeId,
}: CallOptionTypesDialogProps) {
  return (
    <ModalDialog size="lg" isOpen={isOpen} onClose={onClose}>
      <ModalDialogHeader>
        <ModalDialogTitle>How would you like to get started?</ModalDialogTitle>
        <ModalDialogDescription>
          Choose the option that works the best for you
        </ModalDialogDescription>
      </ModalDialogHeader>
      <ModalDialogContent>
        <CardList aria-label="Option types">
          {options.map((option) => (
            <CallOptionItem
              key={option.id}
              icon={option.icon}
              title={option.title}
              description={option.description}
              isRecommended={option.recommended}
              isActive={activeId === option.id}
              action={() => selectOption(option.id)}
              phoneNumber={option.phoneNumber}
              callCenterHours={option.callCenterHours}
            />
          ))}
        </CardList>
      </ModalDialogContent>
      <ModalDialogFooter>
        <ModalDialogActions>
          <Button label="Next" onPress={onNext} isDisabled={isNextDisabled} />
          <Button label="Close" variant="outline" onPress={onClose} />
        </ModalDialogActions>
      </ModalDialogFooter>
    </ModalDialog>
  )
}

interface CallOptionItemProps {
  key: CallOptionId
  icon: IconName
  title: string
  description: string
  isRecommended?: boolean
  isActive: boolean
  action: () => void
  phoneNumber?: string
  callCenterHours?: string
}

function CallOptionItem({
  icon,
  title,
  description,
  isRecommended,
  isActive,
  action,
  phoneNumber,
  callCenterHours,
}: CallOptionItemProps) {
  return (
    <CardListItem onAction={action} isActive={isActive} textValue={title}>
      <CardItemContent
        align="top"
        extrasAlign="top"
        {...(isRecommended && { extras: <LabelChip text="Recommended" /> })}
        icon={<CardItemIcon name={icon} variant="surface-background" />}
      >
        <CardTitle>{title}</CardTitle>
        <CardSubtitle>
          {description}
          {phoneNumber && (
            <p className={styles.optionPhoneNumber}>
              {formatUSPhoneNumber(phoneNumber)}
            </p>
          )}
          {callCenterHours && (
            <p className={styles.optionSchedule}>
              Assistance available: {callCenterHours}
            </p>
          )}
        </CardSubtitle>
      </CardItemContent>
    </CardListItem>
  )
}
